import { Add } from "@mui/icons-material";
import { Box } from "@mui/material";
import { useEffect, useState } from "react";

import ButtonActionsComponent from "../../../components/ButtonActionsComponent";
import ButtonComponent from "../../../components/ButtonComponent";
import ConfirmDeleteModalComponent from "../../../components/ConfirmDeleteModalComponent";
import TableComponent, {
	type IColumnProps,
} from "../../../components/TableComponent";
import SchoolTransportationEditionModal from "./SchoolTransportationEditionModal";
import type { ILocationProps } from "../../../services/Location/types";

import type {
	ISchoolTransportationProps,
	ISchoolTransportationSimpleProps,
	ISchoolTransportationFilterParams,
} from "../../../services/SchoolTransportation/types";
import type { IPaginationParams } from "../../../services/types";

import {
	useDeleteSchoolTransportationAsync,
	useGetSchoolTransportationByIdAsync,
} from "../../../services/SchoolTransportation/hooks/useMutation";
import {
	useGetAllSchoolTransportationByLicenseQuery,
	useGetSchoolTransportationCountQuery,
} from "../../../services/SchoolTransportation/hooks/useQuery";
import { useDebounce } from "../../../services/SchoolTransportation/hooks/useDebounce";

const SchoolTransportationList = () => {
	const [pagination, setPagination] = useState<IPaginationParams>({
		pageNumber: 1,
		pageSize: 10,
	});
	const [searchTerm, setSearchTerm] = useState("");
	const [filter, setFilter] = useState<ISchoolTransportationFilterParams>({
		name: "",
	});
	const debouncedSearchTerm = useDebounce(searchTerm, 500);

	const [isEditionModalOpen, setIsEditionModalOpen] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [itemToDelete, setItemToDelete] =
		useState<ISchoolTransportationSimpleProps | null>(null);
	const [itemToEdit, setItemToEdit] = useState<
		ISchoolTransportationProps | undefined
	>(undefined);
	const [itemToCreateFromVehicle, setItemToCreateFromVehicle] =
		useState<ILocationProps | null>(null);

	const {
		data: transportations = [],
		isLoading,
		isFetching,
	} = useGetAllSchoolTransportationByLicenseQuery(pagination, filter);
	const { data: totalCount = 0, isLoading: isLoadingCount } =
		useGetSchoolTransportationCountQuery(filter);

	const { mutateAsync: deleteTransportationAsync, isLoading: isDeleting } =
		useDeleteSchoolTransportationAsync();
	const { mutateAsync: getDetailsAsync } =
		useGetSchoolTransportationByIdAsync();

	useEffect(() => {
		if (filter.name !== debouncedSearchTerm) {
			setFilter({ name: debouncedSearchTerm });
			setPagination((prev) => ({ ...prev, pageNumber: 1 }));
		}
	}, [debouncedSearchTerm, filter.name]);

	const handleFilterChange = (val: string) => setSearchTerm(val);

	const handleOpenCreateModal = () => {
		setItemToEdit(undefined);
		setItemToCreateFromVehicle(null);
		setIsEditionModalOpen(true);
	};

	const handleOpenEditModal = async (id: number) => {
		try {
			const fullData = await getDetailsAsync(id);
			setItemToCreateFromVehicle(null);
			setItemToEdit(fullData);
			setIsEditionModalOpen(true);
		} catch (error) {
			console.error("Erro ao carregar detalhes", error);
		}
	};

	const handleOpenDeleteModal = (id: number) => {
		const item = transportations.find((t) => t.id === id);
		if (item) {
			setItemToDelete(item);
			setIsDeleteModalOpen(true);
		}
	};

	const handleCloseDeleteModal = () => {
		setIsDeleteModalOpen(false);
		setItemToDelete(null);
	};

	const handleConfirmDeletion = async () => {
		if (!itemToDelete?.id) return;

		await deleteTransportationAsync(itemToDelete.id);
		setIsDeleteModalOpen(false);
		setItemToDelete(null);
	};

	const columns: IColumnProps[] = [
		{
			id: "name",
			label: "Nome",
			width: "30%",
			render: (value) => (
				<span style={{ textTransform: "capitalize" }}>{value}</span>
			),
		},
		{ id: "plate", label: "Placa", width: "20%" },
		{
			id: "brand",
			label: "Marca",
			width: "20%",
			render: (value) => (
				<span style={{ textTransform: "capitalize" }}>
					{value || "Não especificado"}
				</span>
			),
		},
		{
			id: "model",
			label: "Modelo",
			width: "20%",
			render: (value) => (
				<span style={{ textTransform: "capitalize" }}>
					{value || "Não especificado"}
				</span>
			),
		},
		{
			id: "id",
			label: "Ações",
			align: "center",
			width: "8%",
			render: (id) => (
				<ButtonActionsComponent
					key={id}
					isWithDefaultOptionsList
					handleDelete={() => handleOpenDeleteModal(Number(id))}
					handleEdit={() => handleOpenEditModal(Number(id))}
				/>
			),
		},
	];

	return (
		<>
			<Box sx={{ mb: 3 }} />

			<TableComponent
				title="Transporte Escolar"
				rows={transportations}
				columns={columns}
				isLoading={isLoading || isFetching || isLoadingCount}
				totalCount={totalCount}
				onChangePageOrRowsPerPage={setPagination}
				onChangeMainFilter={handleFilterChange}
				searchValue={searchTerm}
				additionalTitleComponent={
					<ButtonComponent onClick={handleOpenCreateModal} startIcon={<Add />}>
						Novo Transporte
					</ButtonComponent>
				}
			/>

			<ConfirmDeleteModalComponent
				title="Excluir Transporte Escolar"
				message={
					itemToDelete
						? `Tem certeza que deseja excluir o transporte "${itemToDelete.name}"?`
						: "Tem certeza?"
				}
				isVisibleDeleteDialog={isDeleteModalOpen}
				setIsVisibleDeleteDialog={setIsDeleteModalOpen}
				isLoading={isDeleting}
				handleConfirmDeletion={handleConfirmDeletion}
				handleCancelDeletion={handleCloseDeleteModal}
			/>

			<SchoolTransportationEditionModal
				isOpen={isEditionModalOpen}
				setIsOpen={setIsEditionModalOpen}
				transportationEdit={itemToEdit}
				initialVehicle={itemToCreateFromVehicle}
			/>
		</>
	);
};

export default SchoolTransportationList;
